<script setup lang="ts">
import { computed, onMounted, ref } from "vue";
import { breakpointsBootstrapV5, useBreakpoints } from "@vueuse/core";

import OrgSettingsLayout from "@Modules/teams/Views/OrgSettingsLayout.vue";
import SettingsTopBar from "@Modules/teams/components/SettingsTopBar.vue";
import SettingsContentBlock from "@Layouts/settings/LayoutBlock.vue";
import { useCurrentOrganization } from "@Modules/teams/composables/useCurrentOrganization";
import SubscriptionsApi, { type BillingPeriod } from "@Modules/subscriptions/Api/SubscriptionsApi";
import OrgPlanInformationCard from "../Views/OrgPlanInformationCard.vue";
import OrgBillingFrequency from "../Views/OrgBillingFrequency.vue";
import OrgPlansSelection from "../Views/OrgPlansSelection.vue";
import OrgSubscriptionCancellation from "../Views/OrgSubscriptionCancellation.vue";

//
// Setup
const { currentOrganizationSlug, currentOrganization, currentWorkspace } = useCurrentOrganization();
const subscriptionsApi = SubscriptionsApi();
const { smallerOrEqual } = useBreakpoints(breakpointsBootstrapV5);

//
// Computed
const isMobileView = smallerOrEqual("xl");

const orgSlug = currentOrganizationSlug;

const currentSubscription = computed(() => currentOrganization.value?.subscription ?? null);

const isEnterprise = computed(() => currentSubscription.value?.enterprise ?? false);

const plansForPeriod = computed(() => {
  // Filter to only show organization plans, then filter by billing period
  return subscriptionsApi.plansForCurrentPeriod.value(billingPeriod.value).filter((plan) => plan.type === "organization");
});

//
// Data
const billingPeriod = ref<BillingPeriod>("monthly");

//
// Methods
const handleSelectPlan = async (planName: string): Promise<void> => {
  if (planName === "enterprise") {
    window.location.href = "/contact";
    return;
  }

  // Find the plan to get its price/currency and type
  const plan = plansForPeriod.value.find((p) => p.name === planName);
  const price = plan?.getPrice(billingPeriod.value);

  if (!plan || !price) {
    return;
  }

  const checkoutOptions = {
    plan: planName,
    type: plan.type,
    interval: billingPeriod.value,
    currency: price.currency,
  };

  // If org already has a subscription (including free), use update endpoint
  // Otherwise use checkout to create new subscription
  if (currentSubscription.value) {
    const success = await subscriptionsApi.updateSubscription(orgSlug.value, checkoutOptions);
    if (success) {
      // Refresh the page to show updated subscription
      window.location.reload();
    }
  } else {
    const session = await subscriptionsApi.initCheckout(orgSlug.value, checkoutOptions);
    if (session?.url) {
      window.location.href = session.url;
    }
  }
};

//
// Lifecycle
onMounted(() => {
  if (currentOrganization.value?.can("manage_subscriptions")) {
    subscriptionsApi.fetchPlans(orgSlug.value);

    // Set initial billing period from current subscription
    if (currentSubscription.value?.interval) {
      billingPeriod.value = currentSubscription.value.interval;
    }
  }
});

//
// Exposed Test Vars
defineExpose({
  billingPeriod,
  handleSelectPlan,
});
</script>

<template>
  <OrgSettingsLayout
    v-if="currentOrganization?.can('manage_subscriptions') || false"
    :organization="currentOrganization"
    :workspace="currentWorkspace"
  >
    <SettingsTopBar title="Subscription" />

    <div
      class="org-subscription-page"
      :class="{
        'org-subscription-page--mobile': isMobileView,
        'org-subscription-page--enterprise': isEnterprise,
      }"
    >
      <SettingsContentBlock class="org-subscription-page__plan-information _plan_information">
        <h6 slot="block-title">
          Plan Information
        </h6>
        <OrgPlanInformationCard
          slot="block-content"
          :subscription="currentSubscription"
          :resume-subscription="async () => { await subscriptionsApi.resumeSubscription(orgSlug); }"
          :cancel-downgrade="async () => { await subscriptionsApi.cancelScheduledDowngrade(orgSlug); }"
          :loading="subscriptionsApi.loading.value"
        />
      </SettingsContentBlock>

      <SettingsContentBlock class="org-subscription-page__plan-options _plan_options">
        <h6 slot="block-title">
          Plan Options
        </h6>
        <template slot="block-content">
          <OrgBillingFrequency
            class="org-subscription-page__billing-frequency _billing_frequency"
            :billing-period="billingPeriod"
            @on-billing-period-update="billingPeriod = $event"
          />
          <OrgPlansSelection
            class="org-subscription-page__plan-selection _plan_selection"
            :plans="plansForPeriod"
            :current-subscription="currentSubscription"
            :billing-period="billingPeriod"
            :loading="subscriptionsApi.loading.value"
            :disabled="!currentOrganization.can('manage_subscriptions')"
            @select-plan="handleSelectPlan"
          />
          <p class="org-subscription-page__tax-disclaimer _tax_disclaimer">
            * Listed prices exclude any applicable taxes.
          </p>
          <OrgSubscriptionCancellation
            class="org-subscription-page__cancellation _cancellation"
            :subscription="currentSubscription"
            :org-slug="orgSlug"
            :loading="subscriptionsApi.loading.value"
          />
        </template>
      </SettingsContentBlock>
    </div>
  </OrgSettingsLayout>
</template>

<style lang="scss" scoped>
@use "@tinyurldev/tu-ui-vue/scss/settings/colors.scss" as color;
@use "@tinyurldev/tu-ui-vue/scss/settings/spacing.scss" as spacing;
@use "@tinyurldev/tu-ui-vue/scss/settings/typography.scss" as typography;

.org-subscription-page {
  $self: &;

  &__plan-information,
  &__billing-frequency,
  &__plan-selection {
    margin-bottom: spacing.$s;
  }

  &__tax-disclaimer {
    color: color.$text-secondary;
    font-size: typography.$xxs-font-size;
    font-style: italic;
    margin-bottom: spacing.$s;
  }

  &--mobile {
    #{$self}__plan-information {
      margin-bottom: spacing.$m;
    }

    #{$self}__billing-frequency {
      margin-bottom: spacing.$xxs;
    }

    #{$self}__tax-disclaimer {
      display: none;
    }
  }

  &--enterprise {
    #{$self}__plan-options {
      h6:first-child,
      #{$self}__title,
      #{$self}__billing-frequency,
      #{$self}__plan-selection,
      #{$self}__cancellation {
        display: none;
      }
    }
  }
}
</style>
